import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';
import type { ChatDoc, FileUploadProgress } from '../types/chat';
import { useChatConfig } from '../context/ChatConfigContext';
import { HtmlContent } from './HtmlContent';
import { DocContent } from './DocContent';
import { collectInlineImageSrcs } from '../utils/inlineImages';
import { getFileIcon, truncateFileName } from '../utils/chatUtils';

interface PendingMessageBubbleProps {
    pendingMessage: { text: string; files: File[]; doc?: ChatDoc; failed?: boolean };
    fileUploadProgress: FileUploadProgress[];
    sending: boolean;
    onRetry: () => void;
    onDismiss: () => void;
    userName?: string;
    userEmail?: string;
}

// The sanitized HTML drops local blob: previews, so a body with inline images renders from its doc.
const PendingBody: React.FC<{ html: string; doc?: ChatDoc }> = ({ html, doc }) => {
    const inlineSrcs = doc ? collectInlineImageSrcs(doc) : {};
    if (!doc || Object.keys(inlineSrcs).length === 0) return <HtmlContent html={html} />;
    return <DocContent doc={doc} resolveFileUrl={(fileId) => inlineSrcs[fileId]} />;
};

const PendingMessageBubble: React.FC<PendingMessageBubbleProps> = ({
    pendingMessage,
    fileUploadProgress,
    sending,
    onRetry,
    onDismiss,
    userName,
    userEmail
}) => {
    const config = useChatConfig();
    const hasFileProgress = fileUploadProgress.length > 0;
    const uploadingFiles =
        hasFileProgress && fileUploadProgress.some((p) => p.status === 'uploading');

    return (
        <div className="flex items-end gap-3 justify-end">
            <div className="flex flex-col min-w-0 max-w-[75%] items-end">
                <div
                    className={`rounded-xl px-4 py-3 break-words overflow-hidden text-sm space-y-2 w-full min-w-0 ${
                        pendingMessage.failed
                            ? 'bg-red-50 border border-red-200 text-red-900'
                            : 'bg-gray-200 text-gray-900'
                    }`}
                >
                    {pendingMessage.text && (
                        <div className="min-w-0 overflow-hidden">
                            <PendingBody html={pendingMessage.text} doc={pendingMessage.doc} />
                        </div>
                    )}
                    {pendingMessage.files.length > 0 && (
                        <div className="flex flex-col gap-1 mt-1.5 -mx-0.5 min-w-0">
                            {pendingMessage.files.map((f, idx) => {
                                const progress = fileUploadProgress[idx];
                                return (
                                    <div
                                        key={`pending-${f.name}-${f.size}-${idx}`}
                                        className={`flex items-center gap-1.5 bg-white border rounded px-2 py-1.5 min-w-0 ${
                                            pendingMessage.failed
                                                ? 'border-red-200'
                                                : progress?.status === 'failed'
                                                  ? 'border-red-200'
                                                  : 'border-gray-200'
                                        }`}
                                    >
                                        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-gray-100 rounded">
                                            <img
                                                src={getFileIcon(f.name)}
                                                alt=""
                                                className="w-3.5 h-3.5 object-contain"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                            <span
                                                className="text-[11px] font-medium text-gray-900 truncate block"
                                                title={f.name}
                                            >
                                                {truncateFileName(f.name)}
                                            </span>
                                            {progress && progress.status === 'uploading' && (
                                                <div className="w-full bg-gray-200 rounded-full h-1 mt-0.5">
                                                    <div
                                                        className="bg-yellow-400 h-1 rounded-full transition-all duration-300"
                                                        style={{ width: `${progress.progress}%` }}
                                                    />
                                                </div>
                                            )}
                                            {progress?.status === 'failed' && (
                                                <span className="text-[10px] text-red-500">
                                                    Upload failed
                                                </span>
                                            )}
                                        </div>
                                        {progress?.status === 'success' && (
                                            <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Timestamp line */}
                <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1 justify-end">
                    {pendingMessage.failed ? (
                        <>
                            <span className="text-red-500 font-medium">Failed to send</span>
                            <span className="text-gray-300">&bull;</span>
                            <button
                                type="button"
                                onClick={onRetry}
                                disabled={sending}
                                className="flex items-center gap-1 text-red-500 hover:text-red-600 font-medium transition-colors disabled:opacity-50"
                            >
                                <ArrowPathIcon className="w-3 h-3" />
                                Retry
                            </button>
                            <span className="text-gray-300">&bull;</span>
                            <button
                                type="button"
                                onClick={onDismiss}
                                className="text-gray-400 hover:text-gray-600 font-medium transition-colors"
                            >
                                Dismiss
                            </button>
                        </>
                    ) : (
                        <>
                            <span>{userName ?? 'You'}</span>
                            <span>&bull;</span>
                            <div className="h-2.5 w-2.5 rounded-full border-[1.5px] border-gray-400 border-t-transparent animate-spin" />
                            <span>{uploadingFiles ? 'Uploading...' : 'Sending...'}</span>
                        </>
                    )}
                </div>
            </div>
            <div className="w-8 h-8 shrink-0">
                {config.renderAvatar({
                    user: { name: userName ?? 'You', email: userEmail ?? '' }
                })}
            </div>
        </div>
    );
};

export { PendingMessageBubble };
